import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import PublicNav from "@/components/n360/PublicNav";
import Footer from "@/components/n360/Footer";
import LayerRadar from "@/components/n360/LayerRadar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BRAND, VALUE_PROPS, ACCESS_LEVELS, GRADING, PROGRAMS, HERO_IMG, ASSEMBLY_IMG, BAND_IMG, PILLAR_IMAGES,
} from "@/lib/content";

const DEMO_SCORES = { relationship: 88, decision: 82, trust: 90, influence: 86, opportunity: 78, risk: 32, knowledge: 84, ai: 74 };

function Eyebrow({ children }) {
  return <div className="eyebrow-line font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-4">{children}</div>;
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl mb-12">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display text-3xl sm:text-4xl text-[#0D3C4C] tracking-tight mb-3">{title}</h2>
      {sub && <p className="text-[#5f7d86] leading-relaxed">{sub}</p>}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, meta } = useAuth();
  const [layer, setLayer] = useState(0);
  const layers = meta?.layers || [];
  const pillars = meta?.pillars || [];

  useEffect(() => {
    const t = setInterval(() => setLayer((l) => (l + 1) % Math.max(layers.length, 1)), 2600);
    return () => clearInterval(t);
  }, [layers.length]);

  const go = () => navigate(user ? "/app" : "/signup");

  return (
    <div className="n360-app-bg n360-grain">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Global leaders in diplomatic summit" className="w-full h-full object-cover opacity-[0.14]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FEF7E7] via-[#FEF7E7]/85 to-[#FEF7E7]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FEF7E7] via-transparent to-transparent" />
          <div className="absolute inset-0 aurora" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7 rise">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full border border-[#F1551D]/30 bg-[#F1551D]/5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-mono-i text-[10px] tracking-[0.25em] text-[#B84214]/90 uppercase">Verified · Confidential · Sovereign</span>
            </div>
            <Eyebrow>India's Trust & Relationship Intelligence Network</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0D3C4C] leading-[1.05]">
              The Sovereign Network of the <span className="gold-text">World's Most Trusted</span> Leaders.
            </h1>
            <p className="mt-6 text-lg text-[#2f5561]/90 max-w-xl leading-relaxed">{BRAND.tagline} {BRAND.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="hero-request-clearance-btn" onClick={go} className="px-7 py-3 rounded-full font-semibold btn-gold">
                {user ? "Enter the Portal" : "Request Clearance"}
              </button>
              <a href="#engine" className="px-7 py-3 rounded-full btn-ghost-gold">Explore the Engine</a>
            </div>
            <div className="mt-12 flex gap-10">
              {[["33,000+", "Verified Leaders"], ["180+", "Countries"], ["6", "Ecosystem Pillars"], ["8", "Intelligence Layers"]].map(([a, b]) => (
                <div key={b}>
                  <div className="font-display text-2xl sm:text-3xl shimmer">{a}</div>
                  <div className="text-xs text-[#5f7d86] tracking-wide">{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Banner imagery */}
          <div className="col-span-12 lg:col-span-5 rise">
            <div className="relative">
              <div className="relative rounded-[1.4rem] overflow-hidden border border-[#EADFC7] shadow-[0_34px_70px_-26px_rgba(13,60,76,0.45)]">
                <img src={HERO_IMG} alt="N360 global leadership summit" className="w-full h-[300px] sm:h-[440px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D3C4C]/55 via-[#0D3C4C]/5 to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur border border-[#EADFC7]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="font-mono-i text-[10px] tracking-widest text-[#0D3C4C] uppercase">Live Network</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                  <div className="rounded-xl overflow-hidden border-2 border-white/80 shadow-lg w-24 h-24 hidden sm:block">
                    <img src={ASSEMBLY_IMG} alt="Global assembly" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-white">
                    <div className="font-mono-i text-[10px] tracking-widest uppercase opacity-80">180+ Countries</div>
                    <div className="font-display text-xl leading-tight">One Network. One Future.</div>
                  </div>
                </div>
              </div>

              {/* floating intelligence radar chip */}
              <div className="absolute -bottom-7 -left-6 w-56 n360-card p-4 hidden md:block">
                <div className="font-mono-i text-[10px] tracking-widest text-[#F1551D]/90 uppercase mb-1">Intelligence Engine</div>
                <LayerRadar layers={layers} scores={DEMO_SCORES} height={148} />
              </div>

              {/* floating stat badge */}
              <div className="absolute -top-5 -right-4 n360-card px-4 py-3 hidden md:block">
                <div className="font-display text-2xl gold-text leading-none">33,000+</div>
                <div className="text-[10px] text-[#5f7d86] tracking-wide">Verified Leaders</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY N360 (image band) */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={BAND_IMG} alt="Executive boardroom" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FEF7E7] via-[#FEF7E7]/85 to-[#FEF7E7]/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Eyebrow>Why N360</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0D3C4C] tracking-tight mb-4">Relationships, verified into opportunity.</h2>
            <p className="text-[#2f5561]/90 leading-relaxed max-w-lg">Confidential matchmaking, trusted deal access and curated intelligence — engineered for the world's most consequential leaders.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {VALUE_PROPS.slice(0, 6).map((v) => {
              const Icon = Icons[v.icon] || Icons.Star;
              return (
                <div key={v.n} className="n360-card p-5 group">
                  <span className="w-10 h-10 rounded-lg border border-[#F1551D]/30 grid place-items-center text-[#F1551D] mb-3 group-hover:bg-[#F1551D]/10 transition-colors"><Icon className="w-5 h-5" /></span>
                  <div className="text-sm text-[#0D3C4C] font-medium leading-snug">{v.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM (image tiles) */}
      <section id="ecosystem" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 scroll-mt-20">
        <SectionTitle eyebrow="Our Ecosystem — 6 Pillars" title="Six pillars. One curated network." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div key={p.id} data-testid={`pillar-card-${p.id}`} className="group relative h-64 rounded-xl overflow-hidden border border-[#F1551D]/18 hover:border-[#F1551D]/50 transition-all duration-300">
              <img src={PILLAR_IMAGES[p.id]} alt={p.name} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FEF7E7] via-[#FEF7E7]/55 to-transparent" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end">
                <span className="font-mono-i text-[11px] text-[#B84214] mb-1">{p.code}</span>
                <h3 className="font-display text-lg text-[#0D3C4C] leading-snug">{p.name}</h3>
                <div className="text-[11px] text-[#5f7d86] mt-1">{p.subSectors.length} sub-sectors</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INTELLIGENCE ENGINE */}
      <section id="engine" className="relative py-20 scroll-mt-20 border-y border-[#F1551D]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle eyebrow="The N360 Intelligence Engine" title="8 layers of verified intelligence." sub="Every fact is attached to a structured entity, linked to its source, dated, and scored for confidence — then synthesised into a living 360° dossier." />
            <div className="grid grid-cols-2 gap-3">
              {layers.map((l, i) => (
                <button key={l.id} onClick={() => setLayer(i)} data-testid={`engine-layer-${l.id}`}
                  className={`text-left px-4 py-3 rounded-lg border transition-colors ${i === layer ? "border-[#F1551D]/50 bg-[#F1551D]/10" : "border-[#F1551D]/15 hover:border-[#F1551D]/30"}`}>
                  <div className="font-mono-i text-[10px] text-[#F1551D]">{l.code}</div>
                  <div className="text-sm text-[#154859]">{l.name}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="n360-card p-8">
            <LayerRadar layers={layers} scores={DEMO_SCORES} height={340} />
            <p className="text-center text-xs text-[#8598a0] mt-2">Illustrative sovereign intelligence radar</p>
          </div>
        </div>
      </section>

      {/* ACCESS LEVELS + GRADING */}
      <section id="access" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 scroll-mt-20">
        <SectionTitle eyebrow="Access Levels & Grading" title="Clearance-tiered, trust-graded." sub="Nothing about a member is visible without authentication. Every field is disclosed only to the appropriate clearance." />
        <div className="grid md:grid-cols-4 gap-5 mb-12">
          {ACCESS_LEVELS.map((a) => (
            <div key={a.name} className="n360-card p-6">
              <div className="font-mono-i text-[11px] text-[#8598a0] mb-1">{a.level}</div>
              <div className="text-[#0D3C4C] font-semibold mb-2">{a.name}</div>
              <p className="text-sm text-[#5f7d86] leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {GRADING.map((g) => (
            <div key={g.tier} className="n360-card p-6 text-center">
              <Icons.Award className={`w-8 h-8 mx-auto mb-3 ${g.tier === "Platinum" ? "text-[#154859]" : g.tier === "Gold" ? "text-[#F1551D]" : g.tier === "Silver" ? "text-[#5f7d86]" : "text-amber-700"}`} />
              <div className="font-display text-lg text-[#0D3C4C]">{g.tier}</div>
              <p className="text-xs text-[#5f7d86] mt-1">{g.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
          {["Discover", "Connect", "Onboard"].map((s, i) => (
            <React.Fragment key={s}>
              <span className="flex items-center gap-2 rounded-full border border-[#F1551D]/25 px-4 py-2 text-[#154859]"><span className="font-mono-i text-[10px] text-[#F1551D]">0{i + 1}</span>{s}</span>
              {i < 2 && <Icons.ChevronRight className="w-4 h-4 text-[#F1551D]/60" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* PROGRAMS (slim strip) */}
      <section id="programs" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <Eyebrow>15 N360 Programs</Eyebrow>
        <div className="flex flex-wrap gap-2.5 mt-4">
          {PROGRAMS.map((p, i) => (
            <span key={p} className="flex items-center gap-2 rounded-full border border-[#F1551D]/18 bg-[#F6EEDC]/60 px-4 py-2 text-sm text-[#2f5561] hover:border-[#F1551D]/45 transition-colors">
              <span className="font-mono-i text-[10px] text-[#F1551D]">{String(i + 1).padStart(2, "0")}</span>{p}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 text-center">
        <Eyebrow>One Network. One Purpose. One Future.</Eyebrow>
        <h2 className="font-display text-3xl sm:text-5xl text-[#0D3C4C] tracking-tight mb-6">Bigger network. Stronger partnerships. <span className="gold-text">Greater impact.</span></h2>
        <p className="text-[#5f7d86] max-w-2xl mx-auto mb-9">Join a curated, verified community of the world's most consequential leaders and institutions.</p>
        <button data-testid="cta-join-btn" onClick={go} className="px-8 py-3.5 rounded-full font-semibold btn-gold">
          {user ? "Enter the Portal" : "Request Your Clearance"}
        </button>
      </section>

      <Footer />
    </div>
  );
}
